import { useCallback, useMemo, useState } from 'react';
import { EmptyState, ModulePage } from '../../shell/kit';
import { useDetail, useResource, useTab } from '../../shell/hooks';
import { sites as loadSites } from '../../api';
import { allowsAnywhere } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import { EmployeeCard } from './card';
import { ListTab } from './list';
import { ProfileTab } from './profile';
import { makeStaffT, type StaffLocale } from './i18n';

/**
 * Раздел «Сотрудники».
 *
 * До него завести кассира можно было только скриптом: маршрутов управления
 * персоналом в ядре не существовало, хотя префикс `/api/v1/employees` был
 * закреплён за ядром с самого начала.
 *
 * Вкладок две, и они разной природы. «Список» — администрирование, живёт под
 * правом `employee.view`. «Мой профиль» — своё, и доступен любому: менять
 * собственный пароль обязан и кассир, у которого прав на чужие учётные записи
 * нет и быть не должно. Поэтому у раздела в `routes.ts` требований нет вовсе,
 * а гейтится каждая вкладка отдельно.
 */

type Tab = 'list' | 'profile';

export default function StaffModule({ t: shellT, user, siteId, title }: ModuleViewProps) {
  const locale: StaffLocale = user.locale === 'kk' ? 'kk' : 'ru';
  const t = useMemo(() => makeStaffT(locale), [locale]);

  const canView = allowsAnywhere(user, 'employee.view', 'employee.manage');
  const tabs: readonly Tab[] = canView ? ['list', 'profile'] : ['profile'];

  const [tab, setTab] = useTab<Tab>('staff', tabs, tabs[0] ?? 'profile');
  const [detail, setDetail] = useDetail('staff', 'list');
  const [reloadKey, setReloadKey] = useState(0);

  const siteList = useResource(() => loadSites(), []);
  const reload = useCallback(() => setReloadKey((v) => v + 1), []);

  /*
   * Пароль, заданный администратором, обязан быть сменён владельцем. Пока это
   * не сделано, показываем профиль и ничего больше: иначе флаг
   * `mustChangePassword` не значил бы ничего — ровно так и было до этого раздела.
   */
  const forced = user.mustChangePassword === true;
  const active: Tab = forced ? 'profile' : tab;

  return (
    <ModulePage
      title={title}
      subtitle={t('staff.subtitle')}
      tabs={(forced ? (['profile'] as const) : tabs).map((key) => ({
        key,
        label: t(`staff.tab.${key}` as never),
      }))}
      activeTab={active}
      onTab={setTab}
    >
      {active === 'list' ? (
        canView ? (
          <>
            <ListTab
              t={t}
              shellT={shellT}
              user={user}
              siteId={siteId}
              sites={siteList.data ?? []}
              reloadKey={reloadKey}
              onOpen={setDetail}
            />
            {detail ? (
              <EmployeeCard
                t={t}
                shellT={shellT}
                user={user}
                employeeId={detail}
                sites={siteList.data ?? []}
                onClose={() => setDetail('')}
                onChanged={reload}
              />
            ) : null}
          </>
        ) : (
          <EmptyState
            title={t('staff.empty')}
            reason="Управление сотрудниками доступно управляющему объекта и администратору сети."
          />
        )
      ) : (
        <ProfileTab
          t={t}
          mustChangePassword={forced}
          totpConfirmed={user.totpVerified}
          onPasswordChanged={reload}
        />
      )}
    </ModulePage>
  );
}
